import {
  DEFAULT_GATEWAY_DRAFT,
  DEFAULT_WEB_TOOLS_DRAFT,
  DEFAULT_ADVANCED_DRAFT,
  OPENCLAW_SECRET_KEYS,
  type AdvancedDraft,
  type GatewayDraft,
  type OpenClawChannelConfig,
  type WebToolsDraft,
  sanitizeProviderId,
} from '@/lib/openclaw-setup';
import type { BotModelsPayload } from '@/lib/use-bot-service';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type OpenClawSetupStep =
  | 'overview'
  | 'channels'
  | 'models'
  | 'gateway'
  | 'webtools'
  | 'workspace'
  | 'review'
  | 'complete';

export interface OpenClawChannelDraft {
  mode: 'shared' | 'own';
  values: Record<string, string>;
}

export interface OpenClawModelDraft {
  type: 'cloud' | 'custom' | 'local';
  cloudProvider: string;
  cloudModelType: string;
  customProviderName: string;
  customEndpoint: string;
  customApiKey: string;
  customModelType: string;
  localPlatform: string;
  localEndpoint: string;
  localApiKey: string;
  localModelType: string;
}

interface OpenClawSetupState {
  currentStep: OpenClawSetupStep;
  selectedChannelId: string | null;
  channelDrafts: Record<string, OpenClawChannelDraft>;
  modelDraft: OpenClawModelDraft;
  gatewayDraft: GatewayDraft;
  webToolsDraft: WebToolsDraft;
  advancedDraft: AdvancedDraft;
  workspacePath: string;
  completed: boolean;
  dismissed: boolean;
  setStep: (step: OpenClawSetupStep) => void;
  setSelectedChannelId: (channelId: string | null) => void;
  updateChannelMode: (channelId: string, mode: 'shared' | 'own') => void;
  updateChannelValue: (channelId: string, key: string, value: string) => void;
  updateModelDraft: (patch: Partial<OpenClawModelDraft>) => void;
  updateGatewayDraft: (patch: Partial<GatewayDraft>) => void;
  updateWebToolsDraft: (patch: Partial<WebToolsDraft>) => void;
  updateAdvancedDraft: (patch: Partial<AdvancedDraft>) => void;
  setWorkspacePath: (path: string) => void;
  hydrateFromServer: (
    channels: Record<string, OpenClawChannelConfig>,
    models: BotModelsPayload | null,
    existingConfig?: Record<string, unknown>,
  ) => void;
  dismissWizard: () => void;
  resumeWizard: () => void;
  markCompleted: () => void;
  resetWizard: () => void;
}

const DEFAULT_MODEL_DRAFT: OpenClawModelDraft = {
  type: 'cloud',
  cloudProvider: import.meta.env.VITE_USE_LOCAL_PROXY !== 'true' ? 'hanggent' : 'anthropic',
  cloudModelType: '',
  customProviderName: 'openai-compatible',
  customEndpoint: '',
  customApiKey: '',
  customModelType: '',
  localPlatform: 'ollama',
  localEndpoint: 'http://localhost:11434',
  localApiKey: '',
  localModelType: '',
};

const DEFAULT_CHANNEL_DRAFT = (): OpenClawChannelDraft => ({
  mode: 'own',
  values: {},
});

function cloneModelDraft(draft: OpenClawModelDraft): OpenClawModelDraft {
  return { ...draft };
}

function omitSecrets(values: Record<string, string>) {
  return Object.fromEntries(
    Object.entries(values).filter(([key]) => !OPENCLAW_SECRET_KEYS.has(key))
  );
}

export const useOpenClawSetupStore = create<OpenClawSetupState>()(
  persist(
    (set) => ({
      currentStep: 'overview',
      selectedChannelId: null,
      channelDrafts: {},
      modelDraft: cloneModelDraft(DEFAULT_MODEL_DRAFT),
      gatewayDraft: { ...DEFAULT_GATEWAY_DRAFT },
      webToolsDraft: { ...DEFAULT_WEB_TOOLS_DRAFT },
      advancedDraft: { ...DEFAULT_ADVANCED_DRAFT },
      workspacePath: '',
      completed: false,
      dismissed: false,
      setStep: (currentStep) => set({ currentStep }),
      setSelectedChannelId: (selectedChannelId) => set({ selectedChannelId }),
      updateChannelMode: (channelId, mode) =>
        set((state) => ({
          channelDrafts: {
            ...state.channelDrafts,
            [channelId]: {
              ...(state.channelDrafts[channelId] ?? DEFAULT_CHANNEL_DRAFT()),
              mode,
            },
          },
        })),
      updateChannelValue: (channelId, key, value) =>
        set((state) => ({
          channelDrafts: {
            ...state.channelDrafts,
            [channelId]: {
              ...(state.channelDrafts[channelId] ?? DEFAULT_CHANNEL_DRAFT()),
              values: {
                ...(state.channelDrafts[channelId]?.values ?? {}),
                [key]: value,
              },
            },
          },
        })),
      updateModelDraft: (patch) =>
        set((state) => ({
          modelDraft: {
            ...state.modelDraft,
            ...patch,
          },
        })),
      updateGatewayDraft: (patch) =>
        set((state) => ({
          gatewayDraft: { ...state.gatewayDraft, ...patch },
        })),
      updateWebToolsDraft: (patch) =>
        set((state) => ({
          webToolsDraft: { ...state.webToolsDraft, ...patch },
        })),
      updateAdvancedDraft: (patch) =>
        set((state) => ({
          advancedDraft: { ...state.advancedDraft, ...patch },
        })),
      setWorkspacePath: (path) => set({ workspacePath: path }),
      hydrateFromServer: (channels, models, existingConfig) =>
        set((state) => {
          const nextChannelDrafts = { ...state.channelDrafts };

          Object.entries(channels).forEach(([channelId, config]) => {
            nextChannelDrafts[channelId] = {
              mode: (config.mode as 'shared' | 'own') ?? nextChannelDrafts[channelId]?.mode ?? 'own',
              values: {
                ...(nextChannelDrafts[channelId]?.values ?? {}),
                ...Object.fromEntries(
                  Object.entries(config).map(([key, value]) => [key, String(value ?? '')])
                ),
              },
            };
          });

          let nextModelDraft = cloneModelDraft(state.modelDraft);

          if (models?.active_model_type === 'cloud' || models?.cloud_model_type) {
            nextModelDraft = {
              ...nextModelDraft,
              type: 'cloud',
              cloudProvider: models.cloud_provider ?? nextModelDraft.cloudProvider,
              cloudModelType: models.cloud_model_type ?? nextModelDraft.cloudModelType,
            };
          }

          const firstCustom = models?.custom_providers?.[0];
          if (models?.active_model_type === 'custom' && firstCustom) {
            nextModelDraft = {
              ...nextModelDraft,
              type: 'custom',
              customProviderName: firstCustom.provider_name ?? nextModelDraft.customProviderName,
              customEndpoint: firstCustom.endpoint_url ?? nextModelDraft.customEndpoint,
              customApiKey: firstCustom.api_key ?? nextModelDraft.customApiKey,
              customModelType: firstCustom.model_type ?? nextModelDraft.customModelType,
            };
          }

          const firstLocal = models?.local_providers?.[0];
          if (models?.active_model_type === 'local' && firstLocal) {
            nextModelDraft = {
              ...nextModelDraft,
              type: 'local',
              localPlatform: sanitizeProviderId(firstLocal.provider_name ?? nextModelDraft.localPlatform),
              localEndpoint: firstLocal.endpoint_url ?? nextModelDraft.localEndpoint,
              localApiKey: firstLocal.api_key ?? nextModelDraft.localApiKey,
              localModelType: firstLocal.model_type ?? nextModelDraft.localModelType,
            };
          }

          // Hydrate gateway, tools, workspace from existing config
          let nextGatewayDraft = { ...state.gatewayDraft };
          let nextWebToolsDraft = { ...state.webToolsDraft };
          let nextAdvancedDraft = { ...state.advancedDraft };
          let nextWorkspacePath = state.workspacePath;

          if (existingConfig) {
            const gw = existingConfig.gateway as Record<string, unknown> | undefined;
            if (gw) {
              if (typeof gw.port === 'number') nextGatewayDraft.port = gw.port;
              if (typeof gw.bind === 'string') {
                if (gw.bind === '127.0.0.1') nextGatewayDraft.bindMode = 'loopback';
                else if (gw.bind === '0.0.0.0') nextGatewayDraft.bindMode = 'lan';
                else {
                  nextGatewayDraft.bindMode = 'custom';
                  nextGatewayDraft.customBind = gw.bind;
                }
              }
            }

            const tools = existingConfig.tools as Record<string, unknown> | undefined;
            const web = tools?.web as Record<string, unknown> | undefined;
            if (web) {
              const search = web.search as Record<string, unknown> | undefined;
              if (search) {
                if (typeof search.enabled === 'boolean') nextWebToolsDraft.searchEnabled = search.enabled;
                if (typeof search.provider === 'string') nextWebToolsDraft.searchProvider = search.provider as WebToolsDraft['searchProvider'];
              }
              const fetch = web.fetch as Record<string, unknown> | undefined;
              if (fetch && typeof fetch.enabled === 'boolean') nextWebToolsDraft.fetchEnabled = fetch.enabled;
            }

            const agents = existingConfig.agents as Record<string, unknown> | undefined;
            const defaults = agents?.defaults as Record<string, unknown> | undefined;
            if (defaults && typeof defaults.workspace === 'string') {
              nextWorkspacePath = defaults.workspace;
            }

            // Hydrate advanced options (tools.profile, acp.dispatch)
            if (typeof tools?.profile === 'string') {
              nextAdvancedDraft.toolsProfile = tools.profile as AdvancedDraft['toolsProfile'];
            }
            const acp = existingConfig.acp as Record<string, unknown> | undefined;
            const dispatch = acp?.dispatch as Record<string, unknown> | undefined;
            if (dispatch && typeof dispatch.enabled === 'boolean') {
              nextAdvancedDraft.acpEnabled = dispatch.enabled;
            }
          }

          return {
            channelDrafts: nextChannelDrafts,
            selectedChannelId:
              state.selectedChannelId ?? Object.keys(channels)[0] ?? state.selectedChannelId,
            modelDraft: nextModelDraft,
            gatewayDraft: nextGatewayDraft,
            webToolsDraft: nextWebToolsDraft,
            advancedDraft: nextAdvancedDraft,
            workspacePath: nextWorkspacePath,
          };
        }),
      dismissWizard: () => set({ dismissed: true }),
      resumeWizard: () => set({ dismissed: false }),
      markCompleted: () => set({ completed: true, dismissed: false }),
      resetWizard: () =>
        set({
          currentStep: 'overview',
          selectedChannelId: null,
          channelDrafts: {},
          modelDraft: cloneModelDraft(DEFAULT_MODEL_DRAFT),
          gatewayDraft: { ...DEFAULT_GATEWAY_DRAFT },
          webToolsDraft: { ...DEFAULT_WEB_TOOLS_DRAFT },
          advancedDraft: { ...DEFAULT_ADVANCED_DRAFT },
          workspacePath: '',
          completed: false,
          dismissed: false,
        }),
    }),
    {
      name: 'openclaw-setup-storage',
      partialize: (state) => ({
        currentStep: state.currentStep,
        selectedChannelId: state.selectedChannelId,
        channelDrafts: Object.fromEntries(
          Object.entries(state.channelDrafts).map(([channelId, draft]) => [
            channelId,
            {
              mode: draft.mode,
              values: omitSecrets(draft.values),
            },
          ])
        ),
        modelDraft: {
          ...state.modelDraft,
          customApiKey: '',
          localApiKey: '',
        },
        gatewayDraft: state.gatewayDraft,
        webToolsDraft: {
          ...state.webToolsDraft,
          searchApiKey: '',
        },
        advancedDraft: state.advancedDraft,
        workspacePath: state.workspacePath,
        completed: state.completed,
        dismissed: state.dismissed,
      }),
    }
  )
);